/**
 * Routes Configuration Base - Partagée entre Frontend et Workers
 * 
 * Contient la configuration minimale des routes sans dépendances spécifiques
 * à l'environnement (pas d'imports FontAwesome, pas de config environment, etc.)
 * 
 * Utilisation:
 * - Frontend: importe cette base et l'enrichit avec icônes, circuit-breaker config
 * - Workers: importe directement cette base
 * 
 * Structure:
 * ```javascript
 * {
 *   route: '/api/service/type',        // Préfixe de route
 *   type: 'SERVICE_TYPE',              // Type de route (enum-like)
 *   workerName: 'Service Name',        // Nom lisible du worker
 *   baseUrl: 'http://localhost:port',  // URL du worker (peut être variable)
 *   keyName: 'env_key_name'            // Nom de la clé API (frontend)
 * }
 * ```
 */

/**
 * Configuration de base des routes
 * Cette liste est la source unique de vérité pour le routage
 * 
 * NOTE: Les URLs (baseUrl) contiennent des références de configuration
 * qui seront remplacées au runtime:
 * - Frontend: utilise getConfig() pour les URLs
 * - Workers: utilisent des URL hardcodées pour les ports dev
 */
export const ROUTES_CONFIG_BASE = [
  // ==================== ROUTES IA ====================
  {
    route: '/api/ia/text',
    type: 'TEXT',
    keyName: 'text_ai_api_key',
    displayName: 'Text AI',
    workerName: 'IA-Proxy Worker - Text',
    // NOTE: baseUrl sera défini par la configuration du contexte
    baseUrl: null,  // À remplacer par config.IA_PROXY_URL (frontend) ou 'http://localhost:65432' (worker)
    translationKey: 'SERVICE_IA_TEXT'
  },
  {
    route: '/api/ia/vision',
    type: 'VISION',
    keyName: 'vision_ai_api_key',
    displayName: 'Vision AI',
    workerName: 'IA-Proxy Worker - Vision',
    baseUrl: null,
    translationKey: 'SERVICE_IA_VISION'
  },
  {
    route: '/api/ia/translation',
    type: 'TRANSLATION',
    keyName: 'translation_ai_api_key',
    displayName: 'Translation AI',
    workerName: 'IA-Proxy Worker - Translation',
    baseUrl: null,
    translationKey: 'SERVICE_IA_TRANSLATION'
  },

  // ==================== ROUTES WA ====================
  {
    route: '/api/wa',
    type: 'WA',
    keyName: 'wa_api_key',
    displayName: 'World Anvil',
    workerName: 'WA-Proxy Worker',
    baseUrl: null,
    translationKey: 'SERVICE_WORLD_ANVIL'
  },

  // ==================== ROUTES CONFIG ====================
  {
    route: '/api/config',
    type: 'CONFIG',
    keyName: 'config_key',
    displayName: 'Configuration',
    workerName: 'Config Worker',
    baseUrl: null,
    translationKey: 'SERVICE_CONFIG'
  },

  // ==================== ROUTES CHAT ====================
  {
    route: '/api/chat',
    type: 'CHAT',
    keyName: 'text_ai_api_key',  // Réutilise la clé text IA
    displayName: 'Chatbot',
    workerName: 'Chatbot Worker',
    baseUrl: null,
    translationKey: 'SERVICE_CHATBOT'
    // NOTE: Les routes /api/rag/* sont aussi routées via ce worker (wab-chatbot)
  }
];

/**
 * Énumération des types de routes disponibles
 * Utilisable pour type-checking
 */
export const ROUTE_TYPES = {
  TEXT: 'TEXT',
  VISION: 'VISION',
  TRANSLATION: 'TRANSLATION',
  WA: 'WA',
  CONFIG: 'CONFIG',
  CHAT: 'CHAT'
};

/**
 * Helper: Obtenir une route par son type
 * @param {string} type - Le type de route (TEXT, VISION, CONFIG, etc.)
 * @returns {Object|null} La configuration de route ou null
 */
export function getRouteByType(type) {
  return ROUTES_CONFIG_BASE.find(config => config.type === type) || null;
}

/**
 * Helper: Obtenir une route par son préfixe
 * @param {string} route - Le préfixe de route (/api/ia/text, /api/config, etc.)
 * @returns {Object|null} La configuration de route ou null
 */
export function getRouteByPath(route) {
  return ROUTES_CONFIG_BASE.find(config => config.route === route) || null;
}

/**
 * Helper: Lister tous les types de routes disponibles
 * @returns {string[]} Array des types de routes
 */
export function getAvailableRouteTypes() {
  return ROUTES_CONFIG_BASE.map(config => config.type);
}

/**
 * Helper: Vérifier qu'un type de route existe
 * @param {string} type - Le type à vérifier
 * @returns {boolean}
 */
export function isValidRouteType(type) {
  return ROUTES_CONFIG_BASE.some(config => config.type === type);
}

/**
 * Helper: Enrichir la config de base avec des propriétés contextuelles
 * Utile pour le frontend qui ajoute les icônes, circuit-breaker config, etc.
 * 
 * @param {Object} baseConfig - Configuration de base d'une route
 * @param {Object} enrichment - Propriétés à ajouter/remplacer
 * @returns {Object} Configuration enrichie
 * 
 * @example
 * const baseRoute = getRouteByType('TEXT');
 * const frontendRoute = enrichRouteConfig(baseRoute, {
 *   serviceIcon: faFileLines,
 *   baseUrl: 'http://localhost:65432',
 *   circuitRoute: '/api/ia/text',
 *   circuitBreakerConfig: { ... }
 * });
 */
export function enrichRouteConfig(baseConfig, enrichment = {}) {
  return {
    ...baseConfig,
    ...enrichment
  };
}

/**
 * Créer une config de route complète pour un contexte donné
 * 
 * @param {string} type - Type de route (TEXT, CONFIG, etc.)
 * @param {Object} context - Contexte {baseUrls, icons?, circuitConfig?}
 * @returns {Object|null} Configuration complète ou null
 * 
 * @example
 * // Frontend
 * const route = buildRouteConfig('TEXT', {
 *   baseUrls: { ia: 'http://localhost:65432' },
 *   icons: { text: faFileLines },
 *   circuitConfig: { failureThreshold: 3 }
 * });
 * 
 * // Worker
 * const route = buildRouteConfig('CONFIG', {
 *   baseUrls: { config: 'http://localhost:63270' }
 * });
 */
export function buildRouteConfig(type, context = {}) {
  const baseRoute = getRouteByType(type);
  if (!baseRoute) {
    return null;
  }

  const enrichment = {
    baseUrl: resolveBaseUrl(baseRoute, context.baseUrls || {}),
    ...(context.icons && { serviceIcon: context.icons[type.toLowerCase()] }),
    ...(context.circuitConfig && { circuitBreakerConfig: context.circuitConfig })
  };

  return enrichRouteConfig(baseRoute, enrichment);
}

/**
 * Helper interne: Résoudre l'URL de base selon le contexte
 */
function resolveBaseUrl(route, baseUrls) {
  // Mapper les types de routes à leurs clés d'URL
  const typeToUrlKey = {
    TEXT: 'ia',
    VISION: 'ia',
    TRANSLATION: 'ia',
    WA: 'wa',
    CONFIG: 'config',
    CHAT: 'chat'
  };

  const urlKey = typeToUrlKey[route.type];
  return baseUrls[urlKey] || route.baseUrl;
}

/**
 * Export par défaut
 */
export default {
  ROUTES_CONFIG_BASE,
  ROUTE_TYPES,
  getRouteByType,
  getRouteByPath,
  getAvailableRouteTypes,
  isValidRouteType,
  enrichRouteConfig,
  buildRouteConfig
};
